// ⚠️ #1522 — the console.warn calls in this file stay warn, deliberately.
//
// main.tsx runs captureConsoleIntegration at levels ["error"], so a warn
// produces no Sentry event. That is the point here: the native accessory-bar shim
// fails on a timer or a shim, nothing she did depends on it, and nothing is
// waiting on the result. The repo's rule is "warn means expected and handled,
// error means a human should see it" — these are the warns that rule exists
// for, and converting them is the quota flood main.tsx's comment warns about.
//
// A failed WRITE she initiated does not belong here. If one is added to this
// file, report it with reportError.

import { useEffect } from "react";

/**
 * Hides the iOS form accessory bar (the gray strip with ‹ › arrows and "Done"
 * that WKWebView draws above the keyboard on every focused input).
 *
 * Native apps — ChatGPT, Claude — don't show it, and it visually breaks the
 * "input sits flush on the keyboard" layout the chat column works hard for.
 * Median exposes `median.keyboard.showAccessoryView(false)` to turn it off at
 * the webview level; once set it applies to every input in the app, so this is
 * a fire-once call, not a per-focus one.
 *
 * ---------------------------------------------------------------------------
 * Issue #331. Deliberately kept OUT of the keyboard measurement path.
 *
 * This hook does not read, write, or observe keyboard height, `--vv-height`,
 * `kin:kb-raw`, or any offset state. It sets one native display flag and stops.
 * That separation is the point: `useKeyboardOffset`, `useStableKeyboardOffset`
 * and ChatInput's layout are load-bearing and read-only for this change, so the
 * accessory bar gets its own file with no shared state to regress.
 *
 * Timing follows the same shape as `useKeyboardOffset`: try the bridge now, and
 * if Median hasn't injected it yet, chain onto `median_library_ready` —
 * CAPTURING the previous handler and calling it first, because several modules
 * compete for that single global and the last one to assign would otherwise
 * silently delete the others.
 *
 * Outside Median (browser, desktop, PWA, Playwright) `window.median` is
 * undefined, every path is guarded by an existence check, and the hook is a
 * no-op — the accessory bar is an iOS-webview artifact that doesn't exist
 * there anyway.
 *
 * On-device verification (Discipline Rule 7): the result is recorded on
 * `window.__kbDiag.accessory` and rendered by the gated debug strip as
 * `acc=<state>`, so a silent no-op is distinguishable from a real hide without
 * tethering the phone. States:
 *   pending   — bridge not there yet, waiting on median_library_ready
 *   hidden    — showAccessoryView(false) returned without throwing
 *   url-fallback — JS function absent on this shell, sent the median:// command
 *   threw:…   — the bridge rejected the call (message included)
 *   no-bridge — not running inside Median; nothing to do
 * ---------------------------------------------------------------------------
 */

type MedianKeyboardAccessory = {
  showAccessoryView?: (show: boolean) => void;
};

/** Module-level so the call survives a StrictMode double-mount or an Index
 *  remount (logout → login) as exactly one native call per page load. */
let applied = false;
let readyChainInstalled = false;

/** `window.median` is declared globally in notifications.ts as
 *  `{ onesignal?: … }`. Re-declaring it here with a `keyboard` member would be
 *  a conflicting duplicate declaration, so — same as useKeyboardOffset — we
 *  narrow at the call site instead of fighting interface merging. */
const getMedian = (): { keyboard?: MedianKeyboardAccessory } | undefined => {
  if (typeof window === "undefined") return undefined;
  return (window as unknown as { median?: { keyboard?: MedianKeyboardAccessory } }).median;
};

const isMedianUserAgent = (): boolean =>
  typeof navigator !== "undefined" && navigator.userAgent.toLowerCase().indexOf("median") > -1;

/** Diagnostics must never throw inside a keyboard-adjacent path — a crash here
 *  would be worse than the accessory bar staying visible. */
const setDiag = (state: string) => {
  try {
    const w = window as any;
    w.__kbDiag = w.__kbDiag || {};
    w.__kbDiag.accessory = state;
  } catch {
    /* ignore */
  }
};

/**
 * Sends the hide command. Returns true only if the native side was actually
 * reached, so the caller knows whether to keep waiting on the bridge.
 * Module-level, not part of any render path.
 */
function hideAccessoryView(): boolean {
  const med = getMedian();
  const kb = med?.keyboard;

  if (typeof kb?.showAccessoryView === "function") {
    try {
      kb.showAccessoryView(false);
      setDiag("hidden");
      console.log("[useHideKeyboardAccessory] median.keyboard.showAccessoryView(false)");
      return true;
    } catch (e) {
      setDiag(`threw:${String(e)}`);
      console.warn("[useHideKeyboardAccessory] showAccessoryView threw:", e);
      return false;
    }
  }

  // Older shells can ship a `median.keyboard` that only has `listen` — the JS
  // library and the native command handler are versioned separately. The
  // median:// URL scheme reaches the native side directly; same fallback
  // useStatusBar.ts uses for exactly this case.
  //
  // Gated on `window.median` EXISTING, not on the user agent: the UA can say
  // "median" while the bridge simply hasn't been injected yet, and the right
  // answer there is to wait for median_library_ready, not to fire a URL scheme
  // blind. It also keeps this out of the browser entirely — e2e/google-signin-
  // median-gate.spec.ts runs a Median UA in real Chromium, and this must stay a
  // no-op there.
  if (med) {
    try {
      window.location.href = "median://keyboard/showAccessoryView?show=false";
      setDiag("url-fallback");
      console.log("[useHideKeyboardAccessory] JS bridge absent — sent median:// command");
      return true;
    } catch (e) {
      setDiag(`threw:${String(e)}`);
      console.warn("[useHideKeyboardAccessory] median:// fallback threw:", e);
      return false;
    }
  }

  return false;
}

export function useHideKeyboardAccessory(): void {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (applied) return;

    // Expose a manual retrigger for Safari Web Inspector sessions, so the
    // accessory bar can be re-tested on a tethered device without a redeploy.
    (window as any).__kinHideAccessory = () => hideAccessoryView();

    if (hideAccessoryView()) {
      applied = true;
      return;
    }

    // Bridge not injected yet (or we're not in Median at all). Queue on
    // median_library_ready — chained, never overwritten.
    if (!readyChainInstalled) {
      readyChainInstalled = true;
      const w = window as unknown as { median_library_ready?: () => void };
      const prevReady = w.median_library_ready;
      w.median_library_ready = () => {
        try {
          prevReady?.();
        } catch {
          /* ignore prior handler errors */
        }
        if (!applied && hideAccessoryView()) applied = true;
      };
    }

    setDiag(isMedianUserAgent() ? "pending" : "no-bridge");
  }, []);
}

export default useHideKeyboardAccessory;
